import _slicedToArray from "/home/runner/work/Project-Evo/Project-Evo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { jsxs, jsx } from 'react/jsx-runtime';
import { Stack, Group, Popover, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FactionIdToName, OtherFaction, BuildAdvantage, ResolveAdvantage, roundContested, roundAdvantage } from '../utils.js';
import useStyles from './WarzoneSelectedItemPanel.styles.js';
import { GetLogoForFaction } from '../../logos/factions/FactionLogo.js';
import { SecurityStatus } from '../../securitystatus/SecurityStatus.js';
import { useWarzoneMap, useWarzoneMapSystem } from '../context/warzone-map.js';
import { useWarzoneSystemStatus } from '../context/warzone-status.js';
import { WarzoneProgressRound } from '../../factionalwarfare/progress/WarzoneProgressRound.js';
import { WarzoneProgress } from '../../factionalwarfare/progress/WarzoneProgress.js';
import { FWSupport } from '../../logos/factionalwarfare/FWSupport.js';
import { Minmatar } from '../../logos/factions/Minmatar.js';
import { Amarr } from '../../logos/factions/Amarr.js';
import { Caldari } from '../../logos/factions/Caldari.js';
import { Gallente } from '../../logos/factions/Gallente.js';
import { FWFrontline } from '../../logos/factionalwarfare/FWFrontline.js';
import { FWSecondline } from '../../logos/factionalwarfare/FWSecondline.js';
import { FWBackline } from '../../logos/factionalwarfare/FWBackline.js';
var Logos = {
  minmatar: Minmatar,
  amarr: Amarr,
  caldari: Caldari,
  gallente: Gallente,
  neutral: FWSupport
};
var AdjacencyLogos = {
  frontline: FWFrontline,
  secondline: FWSecondline,
  backline: FWBackline
};
var WarzoneSelectedItemPanel = function WarzoneSelectedItemPanel(_ref) {
  var classNames = _ref.classNames,
    styles = _ref.styles,
    unstyled = _ref.unstyled,
    _ref$ringSize = _ref.ringSize,
    ringSize = _ref$ringSize === void 0 ? 90 : _ref$ringSize,
    _ref$iconSize = _ref.iconSize,
    iconSize = _ref$iconSize === void 0 ? 40 : _ref$iconSize,
    _ref$advantageTextSiz = _ref.advantageTextSize,
    advantageTextSize = _ref$advantageTextSiz === void 0 ? 18 : _ref$advantageTextSiz,
    children = _ref.children;
  var _useWarzoneMap = useWarzoneMap(),
    activeFactions = _useWarzoneMap.activeFactions,
    selectedSystem = _useWarzoneMap.selectedSystem;
  var _useWarzoneMapSystem = useWarzoneMapSystem(selectedSystem),
    data = _useWarzoneMapSystem.data,
    type = _useWarzoneMapSystem.type,
    adjacency = _useWarzoneMapSystem.adjacency;
  var status = useWarzoneSystemStatus(selectedSystem);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    i18n = _useTranslation.i18n;
  var _useDisclosure = useDisclosure(false),
    _useDisclosure2 = _slicedToArray(_useDisclosure, 2),
    attackerOpened = _useDisclosure2[0],
    _useDisclosure2$ = _useDisclosure2[1],
    attackerClose = _useDisclosure2$.close,
    attackerOpen = _useDisclosure2$.open;
  var _useDisclosure3 = useDisclosure(false),
    _useDisclosure4 = _slicedToArray(_useDisclosure3, 2),
    defenderOpened = _useDisclosure4[0],
    _useDisclosure4$ = _useDisclosure4[1],
    defenderClose = _useDisclosure4$.close,
    defenderOpen = _useDisclosure4$.open;
  var _useDisclosure5 = useDisclosure(false),
    _useDisclosure6 = _slicedToArray(_useDisclosure5, 2),
    resolvedOpened = _useDisclosure6[0],
    _useDisclosure6$ = _useDisclosure6[1],
    resolvedClose = _useDisclosure6$.close,
    resolvedOpen = _useDisclosure6$.open;
  var _useStyles = useStyles({
      ringSize: ringSize
    }, {
      name: "WarzoneSelectedItemPanel",
      classNames: classNames,
      styles: styles,
      unstyled: unstyled
    }),
    classes = _useStyles.classes;
  var hasNoData = useMemo(function () {
    return !selectedSystem || type === "undefined" || !data;
  }, [selectedSystem, type, data]);
  var _useMemo = useMemo(function () {
      var emptyResult = {};
      if (hasNoData) return emptyResult;
      if (!status.found) {
        if (type === "external") {
          var faction = data.reg.faction;
          return {
            defendingFaction: faction,
            defendingFactionName: faction ? FactionIdToName(faction) : ""
          };
        }
        return emptyResult;
      }
      var currentStatus = status.status,
        previousStatus = status.previousStatus;
      var occupierFaction = currentStatus.occupierFaction,
        contestedAmount2 = currentStatus.contestedAmount,
        advantage = currentStatus.advantage,
        contestedStatus2 = currentStatus.contestedStatus;
      var oldContestedAmount = previousStatus.contestedAmount;
      var attackingFaction = OtherFaction(activeFactions, occupierFaction);
      var attackerAdvantage2 = BuildAdvantage(attackingFaction, advantage);
      var defenderAdvantage2 = BuildAdvantage(occupierFaction, advantage);
      var resolvedAdvantage2 = ResolveAdvantage(attackerAdvantage2, defenderAdvantage2);
      return {
        contestedStatus: contestedStatus2,
        contestedAmount: roundContested(contestedAmount2),
        oldContestedAmount: roundContested(oldContestedAmount),
        attackingFaction: attackingFaction,
        attackingFactionName: FactionIdToName(attackingFaction),
        defendingFaction: occupierFaction,
        defendingFactionName: FactionIdToName(occupierFaction),
        attackerAdvantage: attackerAdvantage2,
        defenderAdvantage: defenderAdvantage2,
        resolvedAdvantage: resolvedAdvantage2,
        resolvedAdvantageAmount: resolvedAdvantage2.faction ? roundAdvantage(resolvedAdvantage2.amount) : 0,
        resolvedAdvantageFactionName: resolvedAdvantage2.faction ? FactionIdToName(resolvedAdvantage2.faction) : void 0
      };
    }, [status, type, activeFactions, data, hasNoData]),
    contestedStatus = _useMemo.contestedStatus,
    contestedAmount = _useMemo.contestedAmount,
    attackingFactionName = _useMemo.attackingFactionName,
    defendingFactionName = _useMemo.defendingFactionName,
    attackerAdvantage = _useMemo.attackerAdvantage,
    defenderAdvantage = _useMemo.defenderAdvantage,
    resolvedAdvantage = _useMemo.resolvedAdvantage,
    resolvedAdvantageAmount = _useMemo.resolvedAdvantageAmount,
    resolvedAdvantageFactionName = _useMemo.resolvedAdvantageFactionName;
  var contested = useMemo(function () {
    if (hasNoData) return "";
    switch (contestedStatus) {
      case "Uncontested":
        return t("warzone.status.uncontested");
      case "Contested":
        return t("warzone.status.withPercentage", {
          contestedAmount: contestedAmount.toLocaleString(i18n.language),
          contestedStatus: t("warzone.status.contested")
        });
      case "Vulnerable":
        return t("warzone.status.vulnerable");
      default:
        return t("warzone.status.contested");
    }
  }, [contestedStatus, t, hasNoData, contestedAmount, i18n.language]);
  if (!selectedSystem || type === "undefined" || !data) {
    return /* @__PURE__ */jsxs(Stack, {
      justify: "flex-start",
      spacing: "xs",
      className: classes.root,
      children: [/* @__PURE__ */jsx("p", {
        className: classes.noSelection,
        children: t("warzone.description.nothingSelected")
      }), /* @__PURE__ */jsx("div", {
        children: children
      })]
    });
  }
  var name = data.name,
    sec = data.sec,
    constellation = data.con.name,
    region = data.reg.name;
  if (!status.found && type === "external") {
    return /* @__PURE__ */jsxs(Stack, {
      justify: "flex-start",
      p: 0,
      spacing: "xs",
      className: classes.root,
      children: [/* @__PURE__ */jsxs(Group, {
        spacing: 2,
        children: [/* @__PURE__ */jsx(WarzoneProgressRound, {
          thickness: 6,
          size: ringSize,
          iconSize: iconSize,
          Icon: GetLogoForFaction(defendingFactionName),
          sections: [{
            faction: attackingFactionName,
            value: 0
          }, {
            faction: defendingFactionName,
            value: 0
          }]
        }), /* @__PURE__ */jsxs("div", {
          children: [/* @__PURE__ */jsxs("h2", {
            className: classes.systemName,
            children: [name, " ", /* @__PURE__ */jsx(SecurityStatus, {
              status: sec
            })]
          }), /* @__PURE__ */jsxs("p", {
            className: classes.subHeader,
            children: [constellation, " / ", region]
          })]
        })]
      }), /* @__PURE__ */jsx("div", {
        children: /* @__PURE__ */jsx("h4", {
          className: classes.contestedStatus,
          children: t("warzone.description.notPartOfWarzone")
        })
      }), /* @__PURE__ */jsx("div", {
        children: children
      })]
    });
  }
  var translationArgs = {
    defendingFaction: t("warzone.faction.".concat(defendingFactionName)),
    advantageFaction: resolvedAdvantageFactionName ? t("warzone.faction.".concat(resolvedAdvantageFactionName)) : "",
    advantageAmount: resolvedAdvantageAmount.toLocaleString(i18n.language),
    contestedStatus: contested,
    adjacencyStatus: t("warzone.adjacency.".concat(adjacency))
  };
  var description = t("warzone.adjacency.format", translationArgs);
  var contestedStatusFull = t("warzone.status.ownership", translationArgs);
  var tooltip = t(resolvedAdvantageFactionName ? "warzone.advantage.resolved" : "warzone.advantage.noAdvantage", translationArgs);
  return /* @__PURE__ */jsxs(Stack, {
    justify: "flex-start",
    spacing: "xs",
    className: classes.root,
    children: [/* @__PURE__ */jsxs(Group, {
      spacing: 10,
      style: {
        marginLeft: "-5px"
      },
      children: [/* @__PURE__ */jsx(WarzoneProgressRound, {
        sections: [{
          faction: attackingFactionName,
          value: contestedAmount
        }, {
          faction: defendingFactionName,
          value: 100 - contestedAmount
        }],
        thickness: 5,
        size: ringSize,
        iconSize: iconSize,
        showSeparator: true,
        Icon: AdjacencyLogos[adjacency]
      }), /* @__PURE__ */jsxs("div", {
        children: [/* @__PURE__ */jsxs("h2", {
          className: classes.systemName,
          children: [name, " ", /* @__PURE__ */jsx(SecurityStatus, {
            status: sec
          })]
        }), /* @__PURE__ */jsxs("p", {
          className: classes.subHeader,
          children: [constellation, " / ", region]
        })]
      })]
    }), /* @__PURE__ */jsxs("div", {
      className: classes.info,
      children: [/* @__PURE__ */jsx("p", {
        className: classes.contestedStatus,
        dangerouslySetInnerHTML: {
          __html: contestedStatusFull
        }
      }), /* @__PURE__ */jsx("p", {
        className: classes.description,
        children: description
      }), /* @__PURE__ */jsx("p", {
        className: classes.advantageHeader,
        children: t("warzone.advantage.header")
      }), /* @__PURE__ */jsxs("div", {
        className: classes.advantageContainer,
        children: [/* @__PURE__ */jsxs(Popover, {
          position: "top",
          withArrow: true,
          shadow: "md",
          opened: attackerOpened,
          radius: "xs",
          children: [/* @__PURE__ */jsx(Popover.Target, {
            children: /* @__PURE__ */jsx(WarzoneProgress, {
              onMouseOver: attackerOpen,
              onMouseLeave: attackerClose,
              className: classes.attackerAdvantage,
              sections: [{
                faction: attackingFactionName,
                value: attackerAdvantage.totalAmount
              }]
            })
          }), /* @__PURE__ */jsx(Popover.Dropdown, {
            children: /* @__PURE__ */jsxs(Stack, {
              spacing: 0,
              children: [/* @__PURE__ */jsx(Text, {
                children: t("warzone.advantage.objectives", {
                  amount: attackerAdvantage.dynamicAmount
                })
              }), /* @__PURE__ */jsx(Text, {
                children: t("warzone.advantage.adjacency", {
                  amount: attackerAdvantage.terrainAmount
                })
              })]
            })
          })]
        }), /* @__PURE__ */jsxs(Popover, {
          position: "top",
          withArrow: true,
          shadow: "md",
          opened: defenderOpened,
          radius: "xs",
          children: [/* @__PURE__ */jsx(Popover.Target, {
            children: /* @__PURE__ */jsx(WarzoneProgress, {
              onMouseOver: defenderOpen,
              onMouseLeave: defenderClose,
              className: classes.defenderAdvantage,
              sections: [{
                faction: defendingFactionName,
                value: defenderAdvantage.totalAmount
              }]
            })
          }), /* @__PURE__ */jsx(Popover.Dropdown, {
            children: /* @__PURE__ */jsxs(Stack, {
              spacing: 0,
              children: [/* @__PURE__ */jsx(Text, {
                children: t("warzone.advantage.objectives", {
                  amount: defenderAdvantage.dynamicAmount.toLocaleString(i18n.language)
                })
              }), /* @__PURE__ */jsx(Text, {
                children: t("warzone.advantage.adjacency", {
                  amount: defenderAdvantage.terrainAmount.toLocaleString(i18n.language)
                })
              })]
            })
          })]
        }), /* @__PURE__ */jsxs(Popover, {
          position: "top",
          withArrow: true,
          shadow: "md",
          opened: resolvedOpened,
          width: 225,
          radius: "xs",
          children: [/* @__PURE__ */jsx(Popover.Target, {
            children: /* @__PURE__ */jsx(WarzoneProgressRound, {
              onMouseOver: resolvedOpen,
              onMouseLeave: resolvedClose,
              className: classes.resolvedAdvantage,
              size: ringSize,
              iconSize: iconSize * 0.8,
              thickness: 6,
              sections: [{
                faction: resolvedAdvantageFactionName,
                value: resolvedAdvantage.amount
              }],
              Icon: resolvedAdvantageFactionName ? Logos[resolvedAdvantageFactionName] : FWSupport,
              label: /* @__PURE__ */jsxs(Text, {
                size: advantageTextSize,
                weight: 500,
                color: "#B0B0B0",
                align: "center",
                style: {
                  display: "block",
                  lineHeight: 1.3
                },
                children: [resolvedAdvantage.amount.toLocaleString(i18n.language), "%"]
              })
            })
          }), /* @__PURE__ */jsx(Popover.Dropdown, {
            children: /* @__PURE__ */jsx(Text, {
              children: tooltip
            })
          })]
        })]
      })]
    }), /* @__PURE__ */jsx("div", {
      children: children
    })]
  });
};
export { WarzoneSelectedItemPanel };